import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { parseApiError } from "../../lib/api";
import { useNavigate } from "react-router-dom";
import {
  Search,
  X,
  Plus,
  Star,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
  ExternalLink,
  Pencil,
  Copy,
  Power,
  PowerOff,
  Trash2,
  Filter,
  Inbox,
  Loader2,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import { partnerPreviewActivityUrl } from "../../lib/shopUrls";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PER_PAGE = 10;

const STATUS_OPTIONS = [
  { value: "active", get label() { return tr("Active"); } },
  { value: "deactivated", get label() { return tr("Deactivated"); } },
  { value: "rejected", get label() { return tr("Rejected"); } },
  { value: "draft", get label() { return tr("Draft"); } },
  { value: "deleted", get label() { return tr("Deleted"); } },
];

const STATUS_STYLES = {
  active: "bg-[#D4F7DF] text-[#0F6B2B]",
  deactivated: "bg-[#FFE3A8] text-[#7A4E00]",
  rejected: "bg-[#FFBFBF] text-[#9B0000]",
  draft: "bg-[#E0EAFE] text-[#1F3FA8]",
  deleted: "bg-[#E5E7EB] text-[#4B5563]",
};

const STATUS_LABEL = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label]),
);

const formatProductType = (product) =>
  product.product_type || product.type || "Tour";

/* ---------- Status filter (multi-select popover) ---------- */
const StatusFilter = ({ values, onChange }) => {
  usePartnerTranslation();
  const [open, setOpen] = useState(false);
  const toggle = (val) => {
    if (values.includes(val)) onChange(values.filter((v) => v !== val));
    else onChange([...values, val]);
  };
  const label =
    values.length === 0
      ? tr("All statuses")
      : values.length === 1
        ? STATUS_LABEL[values[0]]
        : `${values.length} statuses`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="products-status-filter"
          className={cn(
            "inline-flex h-11 items-center justify-between gap-2 rounded-lg border bg-white px-3 text-sm transition-colors",
            values.length > 0
              ? "border-[#0F1115] text-[#0F1115]"
              : "border-[#D7DAE0] text-[#0F1115] hover:border-[#0F1115]",
          )}
        >
          <span className="inline-flex items-center gap-2">
            <Filter
              className="h-4 w-4 text-[#6B7280]"
              strokeWidth={1.8}
            />
            {label}
          </span>
          <ChevronDown className="h-4 w-4 text-[#6B7280]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-60 rounded-xl border border-[#EAECEF] p-1 shadow-lg"
      >
        {STATUS_OPTIONS.map((opt) => {
          const active = values.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              data-testid={`status-option-${opt.value}`}
              onClick={() => toggle(opt.value)}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-start text-sm font-medium text-[#0F1115] transition-colors hover:bg-[#F7F7F9]"
            >
              <Checkbox checked={active} className="pointer-events-none" />
              <span>{opt.label}</span>
              <span
                className={cn(
                  "ms-auto inline-block h-2 w-2 rounded-full",
                  STATUS_STYLES[opt.value]?.split(" ")[0],
                )}
              />
            </button>
          );
        })}
        {values.length > 0 && (
          <div className="border-t border-[#EAECEF] pt-1">
            <button
              type="button"
              onClick={() => onChange([])}
              data-testid="status-clear"
              className="block w-full rounded-md px-3 py-2 text-start text-sm font-semibold text-[#FF5757] hover:bg-[#FFF2F2]"
            >{tr("Clear all")}</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/* ---------- Status pill ---------- */
const StatusPill = ({
  status,
  testid
}) => {
  usePartnerTranslation();
  return <span data-testid={testid} className={cn("inline-flex h-6 items-center rounded-full px-2.5 text-[11px] font-semibold", STATUS_STYLES[status] || "bg-[#F3EEEE] text-[#6B7280]")}>
    {STATUS_LABEL[status] || status}
  </span>;
};

/* ---------- Pagination ---------- */
const Pager = ({ page, totalPages, onChange }) => {
  usePartnerTranslation();
  const pages = useMemo(() => {
    if (totalPages <= 1) return [];
    const max = 5;
    if (totalPages <= max)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.min(
      Math.max(1, page - Math.floor(max / 2)),
      totalPages - max + 1,
    );
    return Array.from({ length: max }, (_, i) => start + i);
  }, [page, totalPages]);

  if (totalPages <= 1) return null;
  const btn =
    "inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40";
  return (
    <nav
      aria-label={tr("Pagination")}
      data-testid="products-pagination"
      className="mt-8 flex items-center justify-center gap-1 text-[#0F1115]"
    >
      <button
        type="button"
        onClick={() => onChange(1)}
        disabled={page === 1}
        aria-label={tr("First page")}
        className={cn(btn, "hover:bg-[#F3EEEE]")}
      >
        <ChevronsLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label={tr("Previous page")}
        className={cn(btn, "hover:bg-[#F3EEEE]")}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      {pages[0] > 1 && <span className="px-1 text-sm text-[#9BA1AB]">…</span>}
      {pages.map((p) => {
        const active = p === page;
        return (
          <button
            key={p}
            type="button"
            data-testid={`page-${p}`}
            onClick={() => onChange(p)}
            aria-current={active ? "page" : undefined}
            className={cn(
              btn,
              active ? "text-[#FF5757]" : "text-[#0F1115] hover:bg-[#F3EEEE]",
            )}
          >
            <span
              className={
                active
                  ? "font-extrabold underline underline-offset-4 decoration-2"
                  : ""
              }
            >
              {p}
            </span>
          </button>
        );
      })}
      {pages[pages.length - 1] < totalPages && (
        <span className="px-1 text-sm text-[#9BA1AB]">…</span>
      )}
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        aria-label={tr("Next page")}
        className={cn(btn, "hover:bg-[#F3EEEE]")}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onChange(totalPages)}
        disabled={page === totalPages}
        aria-label={tr("Last page")}
        className={cn(btn, "hover:bg-[#F3EEEE]")}
      >
        <ChevronsRight className="h-4 w-4" />
      </button>
    </nav>
  );
};

/* ---------- Action menu ---------- */
const isAllowed = (status, action) => {
  // Mirrors backend PRODUCT_STATUS_TRANSITIONS
  const map = {
    active: new Set(["edit", "clone", "deactivate", "delete"]),
    deactivated: new Set(["edit", "clone", "activate", "delete"]),
    rejected: new Set(["delete"]),
    draft: new Set(["edit", "delete"]),
    deleted: new Set([]),
  };
  return map[status]?.has(action);
};

const ActionMenu = ({ product, onAction }) => {
  usePartnerTranslation();
  const hasActions = ["edit", "clone", "activate", "deactivate", "delete"].some(
    (action) => isAllowed(product.status, action),
  );
  if (!hasActions) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`row-menu-${product.id}`}
          aria-label={tr("Row actions")}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#6B7280] transition-colors hover:bg-[#F3EEEE] hover:text-[#0F1115]"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-52 rounded-xl border border-[#EAECEF] p-1 shadow-lg"
      >
      {isAllowed(product.status, "edit") && (
        <DropdownMenuItem
          data-testid={`action-edit-${product.id}`}
          onClick={() => onAction("edit", product)}
          className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium"
        >
          <Pencil className="h-4 w-4 text-[#0F1115]" strokeWidth={1.8} />{tr("Edit")}</DropdownMenuItem>
      )}
      {isAllowed(product.status, "clone") && (
        <DropdownMenuItem
          data-testid={`action-clone-${product.id}`}
          onClick={() => onAction("clone", product)}
          className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium"
        >
          <Copy className="h-4 w-4 text-[#0F1115]" strokeWidth={1.8} />{tr("Clone product")}</DropdownMenuItem>
      )}
      {isAllowed(product.status, "activate") && (
        <DropdownMenuItem
          data-testid={`action-activate-${product.id}`}
          onClick={() => onAction("activate", product)}
          className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium"
        >
          <Power className="h-4 w-4 text-[#0F6B2B]" strokeWidth={1.8} />{tr("Activate")}</DropdownMenuItem>
      )}
      {isAllowed(product.status, "deactivate") && (
        <DropdownMenuItem
          data-testid={`action-deactivate-${product.id}`}
          onClick={() => onAction("deactivate", product)}
          className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium"
        >
          <PowerOff className="h-4 w-4 text-[#7A4E00]" strokeWidth={1.8} />{tr("Deactivate")}</DropdownMenuItem>
      )}
      {isAllowed(product.status, "delete") && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid={`action-delete-${product.id}`}
            onClick={() => onAction("delete", product)}
            className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[#9B0000] focus:text-[#9B0000]"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.8} />{tr("Delete product")}</DropdownMenuItem>
        </>
      )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/* ---------- Title cell (title + rating + Preview link) ---------- */
const TitleCell = ({ product, onPreview }) => {
  usePartnerTranslation();
  // Drafts have never been published, so there's nothing to preview yet.
  const canPreview = product.status !== "draft";
  const hasRating = (product.rating || 0) > 0;
  return (
    <div className="min-w-0">
      <p
        data-testid={`title-${product.id}`}
        className="font-semibold text-[#0F1115]"
      >
        {product.title}
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-1 text-xs text-[#6B7280]">
          <Star
            className={cn(
              "h-3 w-3",
              hasRating ? "text-[#FF5757]" : "text-[#CFD2D8]",
            )}
            fill={hasRating ? "#FF5757" : "none"}
            strokeWidth={hasRating ? 0 : 1.8}
          />
          {hasRating ? `${product.rating.toFixed(1)} / 5` : tr("Not rated")}
        </span>
        {canPreview && (
          <button
            type="button"
            data-testid={`preview-${product.id}`}
            onClick={() => onPreview(product)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#2476FF] hover:underline"
          >
            <ExternalLink className="h-3 w-3" />{tr("Preview on website")}</button>
        )}
      </div>
    </div>
  );
};

/* ---------- Delete confirm modal ---------- */
const DeleteConfirmModal = ({ product, onClose, onConfirm }) => {
  usePartnerTranslation();
  const [busy, setBusy] = useState(false);
  if (!product) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm(product);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="delete-product-modal"
        className="max-w-[460px] rounded-2xl border-[#EFE4E4] p-0"
      >
        <DialogHeader className="border-b border-[#EFE4E4] px-6 py-4">
          <DialogTitle className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Delete this product?")}</DialogTitle>
          <DialogDescription className="text-sm text-[#6B7280]">
            <span className="font-semibold text-[#0F1115]">
              {product.title}
            </span>{" "}
            ({product.reference_code}{tr(") will be moved to ")}<em>{tr("Deleted")}</em>{tr(". The product stops accepting new bookings immediately. You can still see it in your list but you can't bring it back.")}</DialogDescription>
        </DialogHeader>
        <div className="rounded-md border-y border-[#FFBFBF] bg-[#FFF5F5] px-6 py-3 text-sm text-[#9B0000]">{tr("This action can't be undone.")}</div>
        <div className="flex items-center justify-end gap-2 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="delete-cancel"
            className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
          >{tr("Keep product")}</button>
          <button
            type="button"
            data-testid="delete-confirm"
            onClick={submit}
            disabled={busy}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-[#9B0000] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#7a0000] disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}{tr("Delete product")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Mobile card ---------- */
const ProductCard = ({
  product,
  onAction,
  onPreview
}) => {
  usePartnerTranslation();
  return <article data-testid={`product-card-${product.id}`} className="rounded-xl border border-[#EAECEF] bg-white p-4">
    <div className="flex items-start justify-between gap-3">
      <TitleCell product={product} onPreview={onPreview} />
      <ActionMenu product={product} onAction={onAction} />
    </div>
    <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">{tr("Type")}</p>
        <p className="mt-0.5 font-semibold text-[#0F1115]">
          {formatProductType(product)}
        </p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">{tr("Product code")}</p>
        <p className="mt-0.5 font-mono text-[#0F1115]">
          {product.reference_code}
        </p>
      </div>
    </div>
    <div className="mt-3 flex justify-end">
      <StatusPill status={product.status} testid={`status-${product.id}`} />
    </div>
  </article>;
};

/* ---------- Main page ---------- */
export default function ProductsPage() {
  usePartnerTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const debounceRef = useRef(null);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => setDebouncedSearch(search.trim()),
      300,
    );
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => setPage(1), [debouncedSearch, statuses]);

  // Fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = { page, per_page: PER_PAGE };
        if (debouncedSearch) params.search = debouncedSearch;
        if (statuses.length) params.status = statuses.join(",");
        const res = await axios.get(`${API}/products`, { params });
        if (cancelled) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.total_pages || 0);
      } catch (err) {
        if (!cancelled) {
          setItems([]);
          setTotal(0);
          setTotalPages(0);
          if (process.env.NODE_ENV !== "production") {
            console.warn("[products] load failed", err);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, statuses]);

  const showingFrom = items.length === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const showingTo = items.length === 0 ? 0 : (page - 1) * PER_PAGE + items.length;
  const hasFilters = !!debouncedSearch || statuses.length > 0;

  const refreshLocal = (updated) => {
    setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  };

  // Map a full ProductDto (returned by clone/activate/deactivate) to the
  // summary shape used by the list so refreshLocal works without a refetch.
  const toDtoSummary = (dto) => ({
    id: dto.id,
    status: dto.status,
    product_type: dto.product_type,
    title: dto.title,
    reference_code: dto.reference_code,
    thumbnail_url: dto.photo_urls?.[0] ?? null,
    option_count: dto.options?.length ?? 0,
    created_at: dto.created_at,
    updated_at: dto.updated_at,
    published_at: dto.published_at,
  });

  const handleAction = async (action, product) => {
    if (action === "edit") {
      navigate(`/products/${product.id}/edit`);
      return;
    }
    if (action === "delete") {
      setConfirmDelete(product);
      return;
    }
    try {
      let res;
      if (action === "clone") {
        res = await axios.post(`${API}/products/${product.id}/clone`);
        toast.success(tr("Product cloned"), { description: res.data.title });
        // Insert summary at top — backend returns full dto; extract summary fields
        const cloned = toDtoSummary(res.data);
        setItems((prev) => [cloned, ...prev]);
        setTotal((t) => t + 1);
        return;
      }
      if (action === "activate") {
        res = await axios.post(`${API}/products/${product.id}/activate`);
        toast.success(tr("Product activated"));
      } else if (action === "deactivate") {
        res = await axios.post(`${API}/products/${product.id}/deactivate`);
        toast.success(tr("Product deactivated"));
      }
      refreshLocal(toDtoSummary(res.data));
    } catch (err) {
      const { message } = parseApiError(err, tr("Please try again."));
      toast.error(tr("Couldn't update product"), { description: message });
    }
  };

  const confirmDeleteAction = async (product) => {
    try {
      await axios.delete(`${API}/products/${product.id}`);
      toast.success(tr("Product deleted"), {
        description: tr("{{value0}} is now in Deleted state.", { value0: product.title }),
      });
      // Mark as deleted locally rather than removing so the row stays visible
      refreshLocal({ ...product, status: "deleted" });
      setConfirmDelete(null);
    } catch (err) {
      const { message } = parseApiError(err, tr("Please try again."));
      toast.error(tr("Couldn't delete product"), { description: message });
    }
  };

  const handlePreview = (product) => {
    window.open(
      partnerPreviewActivityUrl(product.id),
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <main
      data-testid="products-page"
      className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-10 sm:px-6 md:px-10"
    >
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#0F1115] md:text-4xl">{tr("Products")}</h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            {loading
              ? tr("Loading…")
              : total === 0
                ? hasFilters
                  ? tr("No products match your filters")
                  : tr("No products yet")
                : `${total} ${total === 1 ? "product" : "products"}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/create")}
          data-testid="create-new-product"
          className="humrahe-btn-primary inline-flex h-11 items-center gap-2 rounded-lg px-4 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" strokeWidth={2.2} />{tr("Create new product")}</button>
      </header>

      {/* Filters */}
      <section
        data-testid="products-filters"
        className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:gap-3"
      >
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9BA1AB]"
            strokeWidth={1.8}
          />
          <input
            type="text"
            data-testid="products-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr("Search by product or product code")}
            className="humrahe-input !ps-10 !pe-10"
          />
          {search && (
            <button
              type="button"
              data-testid="products-search-clear"
              onClick={() => setSearch("")}
              aria-label={tr("Clear search")}
              className="absolute end-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-[#6B7280] hover:bg-[#F3EEEE]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <StatusFilter values={statuses} onChange={setStatuses} />
        {hasFilters && (
          <button
            type="button"
            data-testid="products-clear-filters"
            onClick={() => {
              setSearch("");
              setStatuses([]);
            }}
            className="inline-flex h-11 items-center gap-2 rounded-lg border border-[#0F1115] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:bg-[#0F1115] hover:text-white"
          >
            <X className="h-4 w-4" />{tr("Clear")}</button>
        )}
      </section>

      {/* Results */}
      <section className="mt-8">
        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#FF5757]" />
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="products-empty"
            className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#EAECEF] bg-[#FAFAFB] py-16 text-center"
          >
            <Inbox className="h-12 w-12 text-[#CFD2D8]" strokeWidth={1.3} />
            <p className="mt-4 text-lg font-semibold text-[#0F1115]">
              {hasFilters
                ? tr("No products match your filters")
                : tr("No products yet")}
            </p>
            <p className="mt-1 max-w-md text-sm text-[#6B7280]">
              {hasFilters
                ? tr("Try a different search or status.")
                : tr("Create your first product to start accepting bookings.")}
            </p>
            {!hasFilters && (
              <button
                type="button"
                onClick={() => navigate("/create")}
                className="humrahe-btn-primary mt-5 inline-flex h-10 items-center gap-2 rounded-md px-4 text-sm font-semibold"
              >
                <Plus className="h-4 w-4" />{tr("Create new product")}</button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto rounded-xl border border-[#EAECEF] bg-white md:block">
              <table className="w-full min-w-[900px] border-collapse text-start">
                <thead>
                  <tr className="border-b border-[#EAECEF] bg-[#FAFAFB] text-[12px] font-semibold uppercase tracking-wider text-[#6B7280]">
                    <th className="px-4 py-3">{tr("Product")}</th>
                    <th className="px-4 py-3">{tr("Type")}</th>
                    <th className="px-4 py-3">{tr("Product code")}</th>
                    <th className="px-4 py-3">{tr("Status")}</th>
                    <th className="px-4 py-3 text-end">{tr("Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr
                      key={p.id}
                      data-testid={`product-row-${p.id}`}
                      className="border-b border-[#F0EFEF] transition-colors hover:bg-[#FFF7F7]"
                    >
                      <td className="px-4 py-4">
                        <TitleCell product={p} onPreview={handlePreview} />
                      </td>
                      <td
                        data-testid={`type-${p.id}`}
                        className="whitespace-nowrap px-4 py-4 text-sm font-semibold text-[#0F1115]"
                      >
                        {formatProductType(p)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 font-mono text-sm text-[#0F1115]">
                        {p.reference_code}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <StatusPill
                          status={p.status}
                          testid={`status-${p.id}`}
                        />
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-end">
                        <ActionMenu product={p} onAction={handleAction} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="grid grid-cols-1 gap-3 md:hidden">
              {items.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onAction={handleAction}
                  onPreview={handlePreview}
                />
              ))}
            </div>

            <Pager page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </section>

      <DeleteConfirmModal
        product={confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={confirmDeleteAction}
      />
    </main>
  );
}
